import { daekyoSpaceAPI } from '@/api';
import CommonSpaceDetailContainer, {
  FieldConfig,
} from '@/components/common/CommonSpaceDetailContainer';
import DaekyoHeader from './DaekyoHeader';
import { palette } from '@/modules/defines/styles';
import { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface DataType {
  id?: number;
  publish: boolean;
  title: string;
  description: string;
  category: number;
  img: string;
  detail_info: any;
  detail_info_order?: string[]; // 섹션 순서 저장용
  link_detail: string;
  link_reserve: string;
  link_viewer: string;
  latlng: string;
  createdAt?: string;
  updatedAt?: string;
}

// Sortable Field Component (for object type)
interface SortableFieldProps {
  id: string;
  fieldKey: string;
  fieldValue: string;
  sectionKey: string;
  updateFieldValue: (
    sectionKey: string,
    fieldKey: string,
    newValue: string
  ) => void;
  removeFieldFromSection: (sectionKey: string, fieldKey: string) => void;
}

const SortableField: React.FC<SortableFieldProps> = ({
  id,
  fieldKey,
  fieldValue,
  sectionKey,
  updateFieldValue,
  removeFieldFromSection,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <FieldRow ref={setNodeRef} style={style}>
      <FieldDragHandle {...attributes} {...listeners}>
        <SmallDragIcon>⋮</SmallDragIcon>
      </FieldDragHandle>
      <FieldLabel>{fieldKey}</FieldLabel>
      <FieldInputWrapper>
        <FieldInput
          value={fieldValue}
          onChange={(e) =>
            updateFieldValue(sectionKey, fieldKey, e.target.value)
          }
          placeholder={`${fieldKey} 값을 입력하세요`}
        />
        <RemoveFieldButton
          type="button"
          onClick={() => removeFieldFromSection(sectionKey, fieldKey)}
        >
          ✕
        </RemoveFieldButton>
      </FieldInputWrapper>
    </FieldRow>
  );
};

// Sortable Array Item Component
interface SortableArrayItemProps {
  id: string;
  index: number;
  item: string;
  sectionKey: string;
  updateArrayItem: (
    sectionKey: string,
    index: number,
    newValue: string
  ) => void;
  removeItemFromArray: (sectionKey: string, index: number) => void;
}

const SortableArrayItem: React.FC<SortableArrayItemProps> = ({
  id,
  index,
  item,
  sectionKey,
  updateArrayItem,
  removeItemFromArray,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ArrayItemRow ref={setNodeRef} style={style}>
      <ArrayDragHandle {...attributes} {...listeners}>
        <SmallDragIcon>⋮</SmallDragIcon>
      </ArrayDragHandle>
      <ArrayIndex>{index + 1}</ArrayIndex>
      <ArrayInputWrapper>
        <ArrayInput
          value={item}
          onChange={(e) => updateArrayItem(sectionKey, index, e.target.value)}
          placeholder={`항목 ${index + 1}을 입력하세요`}
        />
        <RemoveFieldButton
          type="button"
          onClick={() => removeItemFromArray(sectionKey, index)}
        >
          ✕
        </RemoveFieldButton>
      </ArrayInputWrapper>
    </ArrayItemRow>
  );
};

// Sortable Section Component
interface SortableSectionProps {
  id: string;
  sectionKey: string;
  sectionValue: any;
  currentType: 'object' | 'array' | 'string';
  addFieldToSection: (sectionKey: string) => void;
  addItemToArray: (sectionKey: string) => void;
  changeSectionType: (
    sectionKey: string,
    toType: 'object' | 'array' | 'string'
  ) => void;
  removeSection: (sectionKey: string) => void;
  updateFieldValue: (
    sectionKey: string,
    fieldKey: string,
    newValue: string
  ) => void;
  removeFieldFromSection: (sectionKey: string, fieldKey: string) => void;
  updateArrayItem: (
    sectionKey: string,
    index: number,
    newValue: string
  ) => void;
  removeItemFromArray: (sectionKey: string, index: number) => void;
  updateStringValue: (sectionKey: string, newValue: string) => void;
  onFieldOrderChange: (sectionKey: string, fieldKeys: string[]) => void;
  onArrayOrderChange: (sectionKey: string, items: string[]) => void;
}

const SortableSection: React.FC<SortableSectionProps> = ({
  id,
  sectionKey,
  sectionValue,
  currentType,
  addFieldToSection,
  addItemToArray,
  changeSectionType,
  removeSection,
  updateFieldValue,
  removeFieldFromSection,
  updateArrayItem,
  removeItemFromArray,
  updateStringValue,
  onFieldOrderChange,
  onArrayOrderChange,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  // 객체 필드 순서 관리
  const [fieldKeys, setFieldKeys] = useState<string[]>([]);
  // 배열 항목은 인덱스로 관리
  const [arrayItems, setArrayItems] = useState<string[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (currentType === 'object' && typeof sectionValue === 'object') {
      setFieldKeys(Object.keys(sectionValue));
    } else if (currentType === 'array' && Array.isArray(sectionValue)) {
      setArrayItems(sectionValue);
    }
  }, [sectionValue, currentType]);

  const handleFieldDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = fieldKeys.indexOf(active.id as string);
      const newIndex = fieldKeys.indexOf(over.id as string);

      const newFieldKeys = arrayMove(fieldKeys, oldIndex, newIndex);
      setFieldKeys(newFieldKeys);
      onFieldOrderChange(sectionKey, newFieldKeys);
    }
  };

  const handleArrayDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = arrayItems.findIndex(
        (_, i) => `${sectionKey}-item-${i}` === active.id
      );
      const newIndex = arrayItems.findIndex(
        (_, i) => `${sectionKey}-item-${i}` === over.id
      );

      const newArrayItems = arrayMove(arrayItems, oldIndex, newIndex);
      setArrayItems(newArrayItems);
      onArrayOrderChange(sectionKey, newArrayItems);
    }
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <SectionContainer ref={setNodeRef} style={style}>
      <SectionHeader>
        <DragHandle {...attributes} {...listeners}>
          <DragIcon>⋮⋮</DragIcon>
        </DragHandle>
        <SectionTitle>{sectionKey}</SectionTitle>
        <SectionActions>
          {currentType === 'object' && (
            <SmallButton
              type="button"
              onClick={() => addFieldToSection(sectionKey)}
            >
              + 필드 추가
            </SmallButton>
          )}
          {currentType === 'array' && (
            <SmallButton
              type="button"
              onClick={() => addItemToArray(sectionKey)}
            >
              + 항목 추가
            </SmallButton>
          )}
          <TypeButtonGroup>
            <TypeSelectButton
              type="button"
              $active={currentType === 'object'}
              onClick={() => changeSectionType(sectionKey, 'object')}
              title="객체 타입"
            >
              {}
            </TypeSelectButton>
            <TypeSelectButton
              type="button"
              $active={currentType === 'array'}
              onClick={() => changeSectionType(sectionKey, 'array')}
              title="배열 타입"
            >
              [ ]
            </TypeSelectButton>
            <TypeSelectButton
              type="button"
              $active={currentType === 'string'}
              onClick={() => changeSectionType(sectionKey, 'string')}
              title="문자열 타입"
            >
              " "
            </TypeSelectButton>
          </TypeButtonGroup>
          <DeleteButton type="button" onClick={() => removeSection(sectionKey)}>
            삭제
          </DeleteButton>
        </SectionActions>
      </SectionHeader>

      <SectionContent>
        {currentType === 'object' ? (
          // 객체 타입 렌더링 - 드래그 가능
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleFieldDragEnd}
          >
            <SortableContext
              items={fieldKeys}
              strategy={verticalListSortingStrategy}
            >
              {fieldKeys.map((fieldKey) => (
                <SortableField
                  key={fieldKey}
                  id={fieldKey}
                  fieldKey={fieldKey}
                  fieldValue={(sectionValue as Record<string, any>)[fieldKey]}
                  sectionKey={sectionKey}
                  updateFieldValue={updateFieldValue}
                  removeFieldFromSection={removeFieldFromSection}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : currentType === 'array' ? (
          // 배열 타입 렌더링 - 드래그 가능
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleArrayDragEnd}
          >
            <SortableContext
              items={arrayItems.map((_, i) => `${sectionKey}-item-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              {arrayItems.map((item, index) => (
                <SortableArrayItem
                  key={`${sectionKey}-item-${index}`}
                  id={`${sectionKey}-item-${index}`}
                  index={index}
                  item={item}
                  sectionKey={sectionKey}
                  updateArrayItem={updateArrayItem}
                  removeItemFromArray={removeItemFromArray}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          // 문자열 타입 렌더링
          <StringValueWrapper>
            <StringTextArea
              value={sectionValue as string}
              onChange={(e) => updateStringValue(sectionKey, e.target.value)}
              placeholder="내용을 입력하세요"
              rows={4}
            />
          </StringValueWrapper>
        )}
      </SectionContent>
    </SectionContainer>
  );
};

// JSON Editor Component - 구조화된 폼으로 변경
interface JsonEditorProps {
  value: any;
  order?: string[]; // 섹션 순서
  onChange: (value: any) => void;
  onOrderChange?: (order: string[]) => void; // 순서 변경 콜백
}

const JsonEditor: React.FC<JsonEditorProps> = ({
  value,
  order,
  onChange,
  onOrderChange,
}) => {
  const [data, setData] = useState<any>(value || {});
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // 섹션 순서를 관리하기 위한 키 배열
  const [sectionKeys, setSectionKeys] = useState<string[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setData(value || {});
    // 저장된 순서가 있으면 사용, 없으면 Object.keys 순서 사용
    if (order && order.length > 0) {
      setSectionKeys(order);
    } else {
      setSectionKeys(Object.keys(value || {}));
    }
  }, [value, order]);

  useEffect(() => {
    // 컨텐츠 높이가 400px를 초과하는지 체크
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setShowExpandButton(contentHeight > 400);
    }
  }, [data]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sectionKeys.indexOf(active.id as string);
      const newIndex = sectionKeys.indexOf(over.id as string);

      const newKeys = arrayMove(sectionKeys, oldIndex, newIndex);
      setSectionKeys(newKeys);

      // 새로운 순서로 데이터 재구성
      const newData: any = {};
      newKeys.forEach((key) => {
        newData[key] = data[key];
      });
      setData(newData);
      onChange(newData);
      // 순서 변경 콜백 호출
      onOrderChange?.(newKeys);
    }
  };

  // 섹션 추가
  const addSection = () => {
    const sectionName = prompt(
      '섹션 이름을 입력하세요 (예: 기본정보, 어메니티)'
    );
    if (sectionName && sectionName.trim()) {
      const trimmedName = sectionName.trim();
      const newKeys = [...sectionKeys, trimmedName];

      // 순서를 보장하기 위해 sectionKeys 순서대로 객체 재구성
      const newData: any = {};
      newKeys.forEach((key) => {
        newData[key] =
          data[key] !== undefined
            ? data[key]
            : key === trimmedName
            ? {}
            : data[key];
      });

      setData(newData);
      setSectionKeys(newKeys);
      onChange(newData);
      // 순서 변경 콜백 호출
      onOrderChange?.(newKeys);
    }
  };

  // 섹션 삭제
  const removeSection = (sectionKey: string) => {
    if (confirm(`"${sectionKey}" 섹션을 삭제하시겠습니까?`)) {
      const newKeys = sectionKeys.filter((key) => key !== sectionKey);

      // 순서를 보장하기 위해 sectionKeys 순서대로 객체 재구성
      const newData: any = {};
      newKeys.forEach((key) => {
        newData[key] = data[key];
      });

      setData(newData);
      setSectionKeys(newKeys);
      onChange(newData);
      // 순서 변경 콜백 호출
      onOrderChange?.(newKeys);
    }
  };

  // 객체 타입 섹션에 필드 추가
  const addFieldToSection = (sectionKey: string) => {
    const fieldName = prompt('필드 이름을 입력하세요');
    if (fieldName && fieldName.trim()) {
      // 순서를 보장하기 위해 sectionKeys 순서대로 객체 재구성
      const newData: any = {};
      sectionKeys.forEach((key) => {
        if (key === sectionKey) {
          if (typeof data[key] === 'object' && !Array.isArray(data[key])) {
            newData[key] = { ...data[key], [fieldName.trim()]: '' };
          } else {
            newData[key] = data[key];
          }
        } else {
          newData[key] = data[key];
        }
      });

      setData(newData);
      onChange(newData);
    }
  };

  // 객체 타입 섹션의 필드 삭제
  const removeFieldFromSection = (sectionKey: string, fieldKey: string) => {
    // 순서를 보장하기 위해 sectionKeys 순서대로 객체 재구성
    const newData: any = {};
    sectionKeys.forEach((key) => {
      if (key === sectionKey) {
        const updatedSection: any = {};
        Object.keys(data[key]).forEach((fKey) => {
          if (fKey !== fieldKey) {
            updatedSection[fKey] = data[key][fKey];
          }
        });
        newData[key] = updatedSection;
      } else {
        newData[key] = data[key];
      }
    });

    setData(newData);
    onChange(newData);
  };

  // 객체 타입 섹션의 필드 값 변경
  const updateFieldValue = (
    sectionKey: string,
    fieldKey: string,
    newValue: string
  ) => {
    // 순서를 보장하기 위해 sectionKeys 순서대로 객체 재구성
    const newData: any = {};
    sectionKeys.forEach((key) => {
      if (key === sectionKey) {
        newData[key] = { ...data[key], [fieldKey]: newValue };
      } else {
        newData[key] = data[key];
      }
    });

    setData(newData);
    onChange(newData);
  };

  // 배열 타입 섹션에 항목 추가
  const addItemToArray = (sectionKey: string) => {
    // 순서를 보장하기 위해 sectionKeys 순서대로 객체 재구성
    const newData: any = {};
    sectionKeys.forEach((key) => {
      if (key === sectionKey && Array.isArray(data[key])) {
        newData[key] = [...data[key], ''];
      } else {
        newData[key] = data[key];
      }
    });

    setData(newData);
    onChange(newData);
  };

  // 배열 타입 섹션의 항목 삭제
  const removeItemFromArray = (sectionKey: string, index: number) => {
    // 순서를 보장하기 위해 sectionKeys 순서대로 객체 재구성
    const newData: any = {};
    sectionKeys.forEach((key) => {
      if (key === sectionKey && Array.isArray(data[key])) {
        newData[key] = data[key].filter((_, i) => i !== index);
      } else {
        newData[key] = data[key];
      }
    });

    setData(newData);
    onChange(newData);
  };

  // 배열 타입 섹션의 항목 값 변경
  const updateArrayItem = (
    sectionKey: string,
    index: number,
    newValue: string
  ) => {
    // 순서를 보장하기 위해 sectionKeys 순서대로 객체 재구성
    const newData: any = {};
    sectionKeys.forEach((key) => {
      if (key === sectionKey && Array.isArray(data[key])) {
        const updatedArray = [...data[key]];
        updatedArray[index] = newValue;
        newData[key] = updatedArray;
      } else {
        newData[key] = data[key];
      }
    });

    setData(newData);
    onChange(newData);
  };

  // 섹션 타입 변경 (객체 <-> 배열 <-> 문자열)
  const changeSectionType = (
    sectionKey: string,
    toType: 'object' | 'array' | 'string'
  ) => {
    // 순서를 보장하기 위해 sectionKeys 순서대로 객체 재구성
    const newData: any = {};
    sectionKeys.forEach((key) => {
      if (key === sectionKey) {
        if (toType === 'array') {
          newData[key] = [];
        } else if (toType === 'object') {
          newData[key] = {};
        } else {
          newData[key] = '';
        }
      } else {
        newData[key] = data[key];
      }
    });

    setData(newData);
    onChange(newData);
  };

  // 문자열 타입 섹션의 값 변경
  const updateStringValue = (sectionKey: string, newValue: string) => {
    // 순서를 보장하기 위해 sectionKeys 순서대로 객체 재구성
    const newData: any = {};
    sectionKeys.forEach((key) => {
      if (key === sectionKey) {
        newData[key] = newValue;
      } else {
        newData[key] = data[key];
      }
    });

    setData(newData);
    onChange(newData);
  };

  // 객체 필드 순서 변경
  const handleFieldOrderChange = (sectionKey: string, fieldKeys: string[]) => {
    // 순서를 보장하기 위해 sectionKeys 순서대로 객체 재구성
    const newData: any = {};
    sectionKeys.forEach((key) => {
      if (key === sectionKey) {
        const oldObj = data[key] as Record<string, any>;
        const newObj: Record<string, any> = {};
        fieldKeys.forEach((fKey) => {
          newObj[fKey] = oldObj[fKey];
        });
        newData[key] = newObj;
      } else {
        newData[key] = data[key];
      }
    });

    setData(newData);
    onChange(newData);
  };

  // 배열 항목 순서 변경
  const handleArrayOrderChange = (sectionKey: string, items: string[]) => {
    // 순서를 보장하기 위해 sectionKeys 순서대로 객체 재구성
    const newData: any = {};
    sectionKeys.forEach((key) => {
      if (key === sectionKey) {
        newData[key] = items;
      } else {
        newData[key] = data[key];
      }
    });

    setData(newData);
    onChange(newData);
  };

  // 섹션의 현재 타입 확인
  const getSectionType = (value: any): 'object' | 'array' | 'string' => {
    if (Array.isArray(value)) return 'array';
    if (typeof value === 'string') return 'string';
    return 'object';
  };

  return (
    <JsonEditorContainer>
      <JsonEditorHeader>
        <JsonEditorLabel>상세 정보</JsonEditorLabel>
        <AddSectionButton type="button" onClick={addSection}>
          + 섹션 추가
        </AddSectionButton>
      </JsonEditorHeader>

      <JsonDescription>
        섹션별로 정보를 입력하고 수정할 수 있습니다. 섹션 추가 버튼을 클릭하여
        새로운 섹션을 만드세요.
      </JsonDescription>

      {Object.keys(data).length === 0 && (
        <EmptyState>
          섹션이 없습니다. "섹션 추가" 버튼을 클릭하여 정보를 추가하세요.
        </EmptyState>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sectionKeys}
          strategy={verticalListSortingStrategy}
        >
          <SectionsWrapper
            $isExpanded={isExpanded}
            $showExpandButton={showExpandButton}
            ref={contentRef}
          >
            {sectionKeys.map((sectionKey) => {
              const sectionValue = data[sectionKey];
              const currentType = getSectionType(sectionValue);

              return (
                <SortableSection
                  key={sectionKey}
                  id={sectionKey}
                  sectionKey={sectionKey}
                  sectionValue={sectionValue}
                  currentType={currentType}
                  addFieldToSection={addFieldToSection}
                  addItemToArray={addItemToArray}
                  changeSectionType={changeSectionType}
                  removeSection={removeSection}
                  updateFieldValue={updateFieldValue}
                  removeFieldFromSection={removeFieldFromSection}
                  updateArrayItem={updateArrayItem}
                  removeItemFromArray={removeItemFromArray}
                  updateStringValue={updateStringValue}
                  onFieldOrderChange={handleFieldOrderChange}
                  onArrayOrderChange={handleArrayOrderChange}
                />
              );
            })}
          </SectionsWrapper>
        </SortableContext>
      </DndContext>

      {showExpandButton && !isExpanded && Object.keys(data).length > 0 && (
        <ExpandButtonWrapper>
          <ExpandButton type="button" onClick={() => setIsExpanded(true)}>
            펼쳐보기
          </ExpandButton>
        </ExpandButtonWrapper>
      )}

      {showExpandButton && isExpanded && Object.keys(data).length > 0 && (
        <CollapseButtonWrapper>
          <CollapseButton type="button" onClick={() => setIsExpanded(false)}>
            접기
          </CollapseButton>
        </CollapseButtonWrapper>
      )}

      <JsonExampleContainer>
        <JsonExampleTitle>가이드:</JsonExampleTitle>
        <JsonExampleText>
          • <strong>⋮⋮ 드래그</strong>: 섹션, 필드, 항목의 순서를 드래그하여
          변경 가능
          <br />• <strong>섹션</strong>: 정보를 그룹화하는 최상위 카테고리 (예:
          기본정보, 어메니티, 예약 및 문의)
          <br />• <strong>{'{ }'} 객체 타입</strong>: 키-값 쌍으로 구성 (예:
          체크인: 15:00, 체크아웃: 11:00) - 필드 순서 변경 가능
          <br />• <strong>[ ] 배열 타입</strong>: 여러 항목의 리스트 (예:
          어메니티 목록, 이용안내 목록) - 항목 순서 변경 가능
          <br />• <strong>" " 문자열 타입</strong>: 단일 텍스트 값 (예: 예약 및
          문의: tel. 031-589-5600)
          <br />• 타입 버튼을 클릭하여 섹션 타입을 언제든지 변경할 수 있습니다.
        </JsonExampleText>
      </JsonExampleContainer>
    </JsonEditorContainer>
  );
};

const categoryMap: { [key: string]: string } = {
  '1': '객실',
  '2': '다이닝',
  '3': '웨딩&연회',
  '4': '휴식&액티비티',
};

const fields: FieldConfig<DataType>[] = [
  {
    name: 'title',
    label: '이름',
    type: 'text',
    description: '공간의 이름을 입력해주세요',
    width: 'full',
  },
  {
    name: 'description',
    label: '설명',
    type: 'textarea',
    description: '공간에 대한 자세한 설명을 입력해주세요',
    width: 'full',
  },
  {
    name: 'category',
    label: '카테고리',
    type: 'select',
    options: Object.entries(categoryMap).map(([key, value]) => ({
      value: key,
      label: value,
    })),
    description: '공간의 카테고리를 선택해주세요',
    width: 'full',
    isNumber: true,
  },
  {
    name: 'img',
    label: '이미지',
    type: 'image',
    description: '공간의 대표 이미지를 업로드해주세요',
    width: 'full',
  },
  {
    name: 'latlng',
    label: '위도/경도',
    type: 'text',
    placeholder: '위도,경도 형식으로 입력',
    description: '위치 좌표를 입력해주세요 (예: 37.1234,127.5678)',
    width: 'full',
  },
  {
    name: 'link_detail',
    label: '상세 링크',
    type: 'text',
    description: '상세 정보 페이지 링크를 입력해주세요',
    width: 'full',
    showLinkButton: true,
  },
  {
    name: 'link_reserve',
    label: '예약 링크',
    type: 'text',
    description: '예약 페이지 링크를 입력해주세요',
    width: 'full',
    showLinkButton: true,
  },
  {
    name: 'link_viewer',
    label: '뷰어 링크',
    type: 'text',
    description: '3D 뷰어 링크를 입력해주세요',
    width: 'full',
    readOnly: true,
  },
  {
    name: 'detail_info',
    label: '상세 정보 (JSON)',
    type: 'custom',
    description: 'JSON 형식으로 상세 정보를 입력해주세요',
    width: 'full',
    customRender: (formData, handleChange) => (
      <JsonEditor
        value={formData.detail_info}
        order={formData.detail_info_order}
        onChange={(value) => handleChange('detail_info', value)}
        onOrderChange={(order) => handleChange('detail_info_order', order)}
      />
    ),
  },
];

const DaekyoSpaceDetailContainer = () => {
  return (
    <CommonSpaceDetailContainer<DataType>
      HeaderComponent={DaekyoHeader}
      queryKey="getSpaceDaekyo"
      listQueryKey="getSpacesDaekyo"
      queryFn={(id) =>
        daekyoSpaceAPI.getSpace({
          code: import.meta.env.VITE_SECRET_API_KEY,
          id,
        })
      }
      updateFn={(id, data) =>
        daekyoSpaceAPI.updateSpace(
          {
            code: import.meta.env.VITE_SECRET_API_KEY,
            id,
          },
          data
        )
      }
      uploadImageFn={(id, file) => daekyoSpaceAPI.uploadImage(id, file)}
      fields={fields}
      switchFieldKey="publish"
      switchFieldLabel="앱에서 사용"
      defaultRoute="/daekyo/space"
      themeColor={palette.systemBlue}
      themeHoverColor="#0066b3"
    />
  );
};

// Styled Components for JSON Editor
const JsonEditorContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const JsonEditorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const JsonEditorLabel = styled.label`
  font-weight: 600;
  color: #1e293b;
  font-size: 1.1rem;
`;

const AddSectionButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${palette.systemBlue};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s;
  font-weight: 600;

  &:hover {
    background-color: #0066b3;
  }
`;

const JsonDescription = styled.p`
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.5rem;
  margin-bottom: 1rem;
`;

const EmptyState = styled.div`
  padding: 3rem 2rem;
  text-align: center;
  background-color: #f8fafc;
  border: 2px dashed #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  font-size: 0.9rem;
`;

const SectionsWrapper = styled.div<{
  $isExpanded: boolean;
  $showExpandButton: boolean;
}>`
  position: relative;
  max-height: ${(props) => (props.$isExpanded ? 'none' : '400px')};
  overflow: hidden;
  transition: max-height 0.3s ease;

  ${(props) =>
    !props.$isExpanded &&
    props.$showExpandButton &&
    `
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 100px;
      background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
      pointer-events: none;
    }
  `}
`;

const ExpandButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const ExpandButton = styled.button`
  padding: 0.75rem 2rem;
  background-color: white;
  color: #1e293b;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: #f8fafc;
    border-color: #cbd5e1;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
  }
`;

const CollapseButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const CollapseButton = styled.button`
  padding: 0.75rem 2rem;
  background-color: white;
  color: #1e293b;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: #f8fafc;
    border-color: #cbd5e1;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
  }
`;

const SectionContainer = styled.div`
  margin-bottom: 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  gap: 0.75rem;
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  cursor: grab;
  padding: 0.25rem;
  color: #94a3b8;
  transition: color 0.2s;
  flex-shrink: 0;

  &:hover {
    color: #64748b;
  }

  &:active {
    cursor: grabbing;
  }
`;

const DragIcon = styled.span`
  font-size: 1.2rem;
  line-height: 1;
  user-select: none;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  flex: 1;
`;

const SectionActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const SmallButton = styled.button`
  padding: 0.4rem 0.8rem;
  background-color: ${palette.systemBlue};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;

  &:hover {
    background-color: #0066b3;
  }
`;

const TypeButtonGroup = styled.div`
  display: flex;
  gap: 2px;
  background-color: #e2e8f0;
  border-radius: 4px;
  padding: 2px;
`;

const TypeSelectButton = styled.button<{ $active: boolean }>`
  padding: 0.35rem 0.6rem;
  background-color: ${(props) => (props.$active ? 'white' : 'transparent')};
  color: ${(props) => (props.$active ? '#1e293b' : '#64748b')};
  border: none;
  border-radius: 3px;
  font-size: 0.75rem;
  font-weight: ${(props) => (props.$active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s;
  min-width: 32px;
  box-shadow: ${(props) =>
    props.$active ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'};

  &:hover {
    background-color: ${(props) => (props.$active ? 'white' : '#f1f5f9')};
  }
`;

const DeleteButton = styled.button`
  padding: 0.4rem 0.8rem;
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #dc2626;
  }
`;

const SectionContent = styled.div`
  padding: 1rem;
  padding-left: 2rem;
  position: relative;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  position: relative;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FieldDragHandle = styled.div`
  position: absolute;
  left: -24px;
  top: 50%;
  transform: translateY(-50%);
  cursor: grab;
  padding: 0.25rem;
  color: #cbd5e1;
  transition: color 0.2s;

  &:hover {
    color: #94a3b8;
  }

  &:active {
    cursor: grabbing;
  }
`;

const SmallDragIcon = styled.span`
  font-size: 1rem;
  line-height: 1;
  user-select: none;
`;

const FieldLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
  margin-bottom: 0.5rem;
`;

const FieldInputWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const FieldInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: ${palette.systemBlue};
    box-shadow: 0 0 0 2px rgba(0, 122, 204, 0.2);
  }
`;

const RemoveFieldButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background-color: #ef4444;
  color: white;
  border: none;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  flex-shrink: 0;

  &:hover {
    background-color: #dc2626;
  }
`;

const ArrayItemRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.75rem;
  position: relative;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ArrayDragHandle = styled.div`
  cursor: grab;
  padding: 0.25rem;
  color: #cbd5e1;
  transition: color 0.2s;
  flex-shrink: 0;

  &:hover {
    color: #94a3b8;
  }

  &:active {
    cursor: grabbing;
  }
`;

const ArrayIndex = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e2e8f0;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  flex-shrink: 0;
`;

const ArrayInputWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  flex: 1;
`;

const ArrayInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: ${palette.systemBlue};
    box-shadow: 0 0 0 2px rgba(0, 122, 204, 0.2);
  }
`;

const StringValueWrapper = styled.div`
  width: 100%;
`;

const StringTextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.9rem;
  resize: vertical;
  min-height: 80px;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: ${palette.systemBlue};
    box-shadow: 0 0 0 2px rgba(0, 122, 204, 0.2);
  }
`;

const JsonExampleContainer = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #f0f9ff;
  border-radius: 4px;
  border: 1px solid #bae6fd;
`;

const JsonExampleTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #0369a1;
  margin-bottom: 0.5rem;
`;

const JsonExampleText = styled.div`
  font-size: 0.8rem;
  color: #075985;
  line-height: 1.6;

  strong {
    font-weight: 600;
  }
`;

export default DaekyoSpaceDetailContainer;
